import React, { useState, useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import Button from './Button';
import { toast } from 'react-toastify';
import Axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { get } from '../utils/get';
import { ADA_REFERENCE_URL } from '../api/urls';
import useAuth from '../hooks/useAuth';

interface ADAReferenceModalProps {
    closeModal: () => void;
    onADAEvidenceProvided: (adaRef: string, folioNo: string) => void;
}

const ADAReferenceModal = ({ closeModal, onADAEvidenceProvided } : ADAReferenceModalProps) => {
  const [adaReference, setAdaReference] = useState('');
  const [folioNo, setFolioNo] = useState('');
  const [errors, setErrors] = useState({ adaReference: '', folioNo: '' });

  const { user } = useAuth().authState;
  const userId = get(user, "id");
  const accessToken = localStorage.getItem("access_token");

  const validateInputs = () => {
    let isValid = true;
    let newErrors = { adaReference: '', folioNo: '' };

    if (!adaReference) {
      newErrors.adaReference = 'File No. is required';
      isValid = false;
    }

    if (!folioNo) {
      newErrors.folioNo = 'Folio No. is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateInputs()) {
        return;
    }

    console.log('File No.:', adaReference);
    console.log('Folio No:', folioNo);

    try {
        const adaData = {
            ada_ref: adaReference,
            folio_no: folioNo,
            user_id: userId,
        };

        const response = await Axios.post(
            ADA_REFERENCE_URL,
            adaData,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );

        if (response.status === 201) {
            toast.success('ADA Evidence uploaded successfully');
            onADAEvidenceProvided(adaReference, folioNo);
            closeModal();
        } else {
            toast.error('Failed to upload ADA evidence');
        }
    } catch (error) {
        console.error('Error inserting ADA reference:', error);
        toast.error('An error occurred while uploading your ADA evidence');
    }
  };

  return (
    <ModalWrapper>
      <ModalContent>
        <CloseButton onClick={closeModal}>&times;</CloseButton>
        <ModalTitle>ADA Evidence</ModalTitle>

        <InputField
          type="text"
          value={adaReference}
          onChange={(e) => setAdaReference(e.target.value)}
          placeholder="File No."
          aria-label="File Number"
        />
        {errors.adaReference && <ErrorText>{errors.adaReference}</ErrorText>}

        <InputField
          type="text"
          value={folioNo}
          onChange={(e) => setFolioNo(e.target.value)}
          placeholder="Folio No."
          aria-label="Folio Number"
        />
        {errors.folioNo && <ErrorText>{errors.folioNo}</ErrorText>}

        <ButtonContainer>
          <Button text="Submit" onClick={handleSubmit} />
          <Button text="Cancel" onClick={closeModal} backgroundColor='red' hoverBackgroundColor='red' />
        </ButtonContainer>
      </ModalContent>
    </ModalWrapper>
  );
};

// Styled Components
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 100%;
  position: relative;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalTitle = styled.h2`
  margin-bottom: 1rem;
  color: #333;
`;

const InputField = styled.input`
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const ErrorText = styled.p`
  color: red;
  font-size: 0.875rem;
  margin: -0.5rem 0 1rem;
`;

export default ADAReferenceModal;
